<template>
  <!-- prettier-ignore -->
  <div
    class="query"
  >
    <basic-text-input
      ref="inputRef"
      v-model="selectedLabel"
      :container-style="{ 'anchor-name': `--${dropdownId}` }"
      manual-validation
      :name="$props.name"
      :disabled="$props.disabled"
      :label="$props.label"
      :required="$props.required"
      :size="$props.size"
      :supporting-text="$props.supportingText"
      :v="$props.v"
      @clear="doClear"
      @keydown="onKeyDown"
      @input.stop="search"
    >
      <template #leading>
        <slot name="leading" />
      </template>

      <template #trailing>
        <the-icon
          v-if="isLoading"
          class="fa-spin"
          art="duo"
          name="spinner-third"
        />
      </template>
    </basic-text-input>

    <basic-dropdown-menu
      :id="dropdownId"
      ref="dropdownRef"
      manual-focus
      name="query-dropdown"
      :options="$props.results"
      :selected="model"
      :size="$props.size"
      @close="onClose"
      @select="selectItem"
    />
  </div>
</template>

<script setup>
  import { nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
  import { v4 as uuidv4 } from 'uuid'
  import { debounce } from 'lodash'

  import { ReactivityUtil } from '@shared/utils/Reactivity'

  import BasicDropdownMenu from '@shared/components/Basic/DropdownMenu'
  import BasicTextInput from '@shared/components/Basic/TextInput'

  import { DEFAULT_FORM_DEBOUNCE_TIME } from '@shared/config/constants'

  // INIT
  const emit = defineEmits(['search'])
  const model = defineModel({ type: [Object, String] })
  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
    },

    flatLabel: {
      type: Boolean,
      default: false
    },

    hasTrailing: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      required: true,
    },

    labelKey: {
      type: String,
      default: 'label',
    },

    name: {
      type: String,
      required: true,
    },

    results: {
      type: Array,
      default: () => [],
    },

    required: {
      type: Boolean,
      default: false,
    },

    size: {
      validator(value) {
        return ['sm', 'md'].includes(value)
      },
      default: 'sm',
    },

    supportingText: {
      type: String,
      default: undefined,
    },

    v: {
      type: Object,
      default: () => {},
    },

    value: {
      type: Object,
      default: () => {},
    },
  })

  // DATA
  const KEY = uuidv4()
  const dropdownId = `dropdown-${KEY}`
  const dropdownRef = useTemplateRef('dropdownRef')
  const inputRef = useTemplateRef('inputRef')
  const selectedLabel = ref(null)

  // METHODS
  function doClear() {
    model.value = null
  }

  async function onClose() {
    await nextTick()

    if (!props.flatLabel && !model.value) {
      selectedLabel.value = null
    }
  }

  /**
   * handle keydown-events to set focus to opened dropdown menu
   */
  function onKeyDown(event) {
    if (dropdownRef.value.isOpen()) {
      switch (event.code) {
        case 'Tab':
        case 'ArrowUp':
        case 'ArrowDown':
          dropdownRef.value.setFocus()
          event.preventDefault()
          break
      }
    }
  }

  /**
   * select item and then set focus on the input-field (to restore tabbing order)
   */
  function selectItem(item) {
    selectedLabel.value = item.label

    if (model.value === undefined || model.value === null) {
      model.value = item.value
    } else {
      ReactivityUtil.reAssign(model.value, item.value)
    }

    dropdownRef.value.close()
    inputRef.value.inputRef.focus()
  }

  const search = debounce(_search, DEFAULT_FORM_DEBOUNCE_TIME)
  function _search(event) {
    model.value = null
    props.v?.$reset()

    emit('search', event.target.value.replace(/-+/g, '-'))
  }

  /**
   * blur is prevented, when value is selected from dropdown.
   * usefull to keep text-value from input, e.g. street- and streetNumber-search
   */
  function onBlur(event) {
    window.setTimeout(() => {
      const hasDropDownFocus = document.activeElement.closest(`#${dropdownRef.value.elementRef.getAttribute('id')}`)
      const hasFocus = event.target === document.activeElement || hasDropDownFocus

      if (event.target.value !== model.value && !hasFocus) {
        model.value = event.target.value
      }
    }, 100)
  }

  // WATCHERS
  watch(
    model,
    value => {
      if (!value) return

      selectedLabel.value = (props.flatLabel) ? value : value[props.labelKey]
    },
    { immediate: true, deep: true }
  )

  watch(props.results, values => {
    if (values.length > 0) {
      dropdownRef.value.open()
    } else {
      dropdownRef.value.close()
    }
  })

  // LIFECYCLE HOOKS
  onMounted(() => {
    if (props.flatLabel) {
      inputRef.value.inputRef.addEventListener('blur', event => onBlur(event))
    }

    if (props.value && props.value[props.labelKey]) {
      selectedLabel.value = props.flatLabel ? props.value : props.value[props.labelKey]
    }
  })
</script>

<style scoped>
  .query {
    width: 100%;
  }

  .location {
    position: relative;

    .icon {
      color: var(--on-surface);
    }
  }
</style>
